import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main>
          <div className="pt-20 pb-10 bg-background-100">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: '全部文章', to: '/category/all' },
                  { label: '文章不存在' }
                ]}
              />
            </div>
          </div>
          <section className="py-20 bg-background-50">
            <div className="max-w-[800px] mx-auto px-4 md:px-6 text-center">
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-6">
                <i className="ri-error-warning-line text-3xl"></i>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">
                文章不存在
              </h1>
              <p className="text-foreground-500 mb-6">您访问的文章可能已被移除或链接错误</p>
              <Link
                to="/category/all"
                className="inline-flex px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                返回文章列表
              </Link>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '挣扎吧亚当君制作组'
    },
    publisher: {
      '@type': 'Organization',
      name: '挣扎吧亚当君制作组'
    },
    image: article.coverImage,
    url: `https://playadventure.github.io/article/${article.slug}`,
    inLanguage: 'zh-CN'
  };

  const paragraphs = article.content
    .split('\n\n')
    .filter((p) => p.trim() !== '');

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
      <ScrollToTop />
      <Navbar />
      <main>
        <article>
          <div className="pt-20 pb-10 bg-background-100">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: '全部文章', to: '/category/all' },
                  { label: article.categoryName, to: `/category/${article.category}` },
                  { label: article.title }
                ]}
              />
            </div>
          </div>

          <header className="bg-background-100 pb-8">
            <div className="max-w-[800px] mx-auto px-4 md:px-6">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                {article.categoryName}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span>{article.date}</span>
                <span className="text-foreground-300">·</span>
                <span>{article.readTime}</span>
                <span className="text-foreground-300">·</span>
                <span>挣扎吧亚当君制作组</span>
              </div>
            </div>
          </header>

          <div className="max-w-[800px] mx-auto px-4 md:px-6 py-10">
            <div className="relative w-full h-[400px] rounded-lg overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-content">
              {paragraphs.map((para, index) => {
                if (para.startsWith('## ')) {
                  return (
                    <h2 key={index} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                      {para.replace('## ', '')}
                    </h2>
                  );
                }
                if (para.startsWith('### ')) {
                  return (
                    <h3 key={index} className="font-heading text-lg font-bold text-foreground-800 mt-8 mb-3">
                      {para.replace('### ', '')}
                    </h3>
                  );
                }
                if (para.startsWith('1. ') || para.startsWith('2. ') || para.startsWith('3. ') || para.startsWith('4. ')) {
                  return (
                    <p key={index} className="text-foreground-700 text-base leading-relaxed mb-2 pl-4 border-l-2 border-primary-300">
                      {para}
                    </p>
                  );
                }
                if (para.startsWith('- ') || para.startsWith('**')) {
                  const cleanText = para.replace(/^\*\*/, '').replace(/\*\*$/, '');
                  return (
                    <p key={index} className="text-foreground-700 text-base leading-relaxed mb-2 pl-4">
                      <span className="text-primary-600 font-medium">• </span>
                      {cleanText.replace('- ', '')}
                    </p>
                  );
                }
                return (
                  <p key={index} className="text-foreground-700 text-base leading-relaxed mb-5">
                    {para}
                  </p>
                );
              })}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回文章列表
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}